import React, { useRef, useEffect, memo } from "react";
import * as d3 from "d3";

interface HeatMapProps {
  parentid: string;
  data?: any;
  labels?: string[];
}

// const generateHeatmapData = (horizontalCells: number, verticalCells: number) => {
//   const data: any = [];
//   for (let i = 0; i < verticalCells; i++) {
//     const row = [];
//     for (let j = 0; j < horizontalCells; j++) {
//       const value1 = parseFloat((Math.random() * 100).toFixed(0));
//       const value2 = parseFloat((Math.random() * 100).toFixed(0));
//       row.push([value1, value2]);
//     }
//     data.push(row);
//   }
//   return data;
// };

// const randomData = generateHeatmapData(24, 7);

const weekNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const HeatMap: React.FC<HeatMapProps> = memo(({ parentid, data, labels }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const randomData = data;

  const scale = window.innerWidth / 1920;

  useEffect(() => {
    if (randomData && randomData.length > 0) {
      if (!svgRef.current) return;

      d3.select(svgRef.current).selectAll("*").remove();

      // Set dimensions and margins
      const margin = { top: 10, right: 10, bottom: 10, left: 50 };
      const parentElement = document.getElementById(parentid);
      if (!parentElement) {
        // Handle the error
        console.error(`No element with ID ${parentid}`);
        return;
      }

      const rect = parentElement.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      // Create SVG element
      const svg = d3
        .select(svgRef.current)
        .attr("width", width)
        .attr("height", height)
        .append("g")
        .attr(
          "transform",
          `translate(${margin.left}, ${-margin.bottom - margin.top})`,
        );

      const rows = randomData.length;
      const cols = randomData[0].length;

      const xAxisItems = [];
      for (let i = 0; i < cols; i++) {
        xAxisItems.push((i + 1).toString());
      }

      const yaxisItems = [];
      for (let i = 0; i < rows; i++) {
        yaxisItems.push((i + 1).toString());
      }

      const chartData = [];
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          let value = 0;
          randomData[i][j].forEach((val: number) => {
            value += val;
          });
          chartData.push({
            x: (j + 1).toString(),
            y: (i + 1).toString(),
            value: value,
            data: randomData[i][j],
          });
        }
      }

      // Build X scales and axis:
      const x = d3
        .scaleBand()
        .range([0, width - margin.left - margin.right])
        .domain(xAxisItems)
        .padding(0.05);

      svg
        .append("g")
        .attr("transform", "translate(0," + height + ")")
        .call(d3.axisBottom(x))
        .selectAll("text")
        .text((d: any) => d - 1);

      // Build X scales and axis:
      const y = d3
        .scaleBand()
        .range([height - margin.top - margin.bottom, 0])
        .domain(yaxisItems)
        .padding(0.05);

      svg
        .append("g")
        .call(d3.axisLeft(y))
        .attr("transform", "translate(0," + (margin.top + margin.bottom) + ")")
        .selectAll("text")
        .text((d: any) => weekNames[parseInt(d) - 1]);

      let maxv = d3.max(chartData, (d) => d.value) ?? 0;

      if (maxv === 0) maxv = 100;

      // Build color scale
      const myColor = d3.scaleLinear().domain([0, maxv]).range([0, 1]);

      svg
        .append("clipPath")
        .attr("id", "clip-rect")
        .append("rect")
        .attr("x", 0)
        .attr("y", -100)
        .attr("width", width)
        .attr("height", height + 100);

      const rects = svg
        .append("g")
        // .attr("clip-path", "url(#clip-rect)")
        .selectAll()
        .data(chartData, function (d: any) {
          return d.x + ":" + d.y;
        })
        .enter()
        .append("rect")
        .attr("x", (d: any) => String(x(d.x)))
        .attr("y", (d: any) => String(y(d.y)))
        .attr("width", x.bandwidth())
        .attr("height", y.bandwidth())
        .style("cursor", "pointer")
        .style("stroke-width", "0.5px")
        .style("stroke", "var(--secondary)")
        .style("fill-opacity", 0)
        .style("fill", function () {
          return "rgb(105, 179, 162";
        })
        .attr("transform", function (d: any) {
          const rectX = (x(d.x) || 0) + x.bandwidth() / 2;
          const rectY = (y(d.y) || 0) + y.bandwidth() / 2;
          return `translate(${rectX}, ${rectY}) scale(1) translate(${-rectX}, ${-rectY}) translate(0, ${margin.top + margin.bottom})`;
        });

      const yoffset = 100;
      const xoffset = -40;
      const duration = 1000;
      const factor = 10;
      const offset = 1000;

      rects
        .style("fill-opacity", 0)
        .style("opacity", 0)
        .attr("transform", function (d: any) {
          const rectX = (x(d.x) || 0) + xoffset + x.bandwidth() / 2;
          const rectY = (y(d.y) || 0) + yoffset + y.bandwidth() / 2;
          return `translate(${rectX}, ${rectY}) scale(1.5) translate(${-rectX}, ${-rectY}) translate(0, ${margin.top + margin.bottom})`;
        });

      rects
        .transition()
        .duration(duration)
        .ease(d3.easeCubicInOut)
        .delay(function (d) {
          return parseInt(d.x) * (7 - parseInt(d.y)) * factor + offset;
        })
        .style("fill-opacity", (d: any) => myColor(d.value))
        .style("opacity", 1)
        .attr("transform", function (d: any) {
          const rectX = (x(d.x) || 0) + xoffset + x.bandwidth() / 2;
          const rectY = (y(d.y) || 0) + yoffset + y.bandwidth() / 2;
          return `translate(${rectX}, ${rectY}) scale(1) translate(${-rectX}, ${-rectY}) translate(0, ${margin.top + margin.bottom})`;
        });

      rects
        .on("mouseenter", function (_, i) {
          const info = d3.select("#infoRec_" + i.x + "_" + i.y);
          info.transition().duration(200).style("opacity", 1);

          d3.select(this).transition().duration(100).style("stroke", "white");
        })
        .on("mouseout", function (_, i) {
          const info = d3.select("#infoRec_" + i.x + "_" + i.y);
          info.transition().duration(200).style("opacity", 0);

          d3.select(this)
            .transition()
            .duration(100)
            .style("stroke", "var(--secondary)");
        });

      const groups = svg
        .selectAll()
        .data(chartData, function (d: any) {
          return d.x + ":" + d.y;
        })
        .enter()
        .append("g")
        .attr("opacity", 0)
        .attr("id", (d) => "infoRec_" + d.x + "_" + d.y);

      labels?.forEach((label, index) => {
        groups
          .append("text")
          .attr("x", (d: any) => String(x(d.x)))
          .attr("y", (d: any) => String((y(d.y) ?? 0) + index * 20 * scale))
          .style("cursor", "pointer")
          .style("fill", "var(--background)")
          .style("opacity", 1)
          .text((d) => {
            return label + ": " + d.data[index];
          })
          .style("pointer-events", "none")
          .style("font-size", "0.7rem")
          .attr("class", "labelText")
          .style("text-anchor", "start")
          .attr("transform", "translate(-25,-15)");
      });

      groups
        .insert("rect", ":first-child")
        .attr("x", (d: any) => String(x(d.x)))
        .attr("y", (d: any) => String(y(d.y)))
        .attr("width", () => {
          const texts = svg.selectAll(".labelText");
          let maxWidth = 0;
          texts.nodes().forEach((nd: any) => {
            const w = nd.getBBox().width;
            if (w > maxWidth) {
              maxWidth = w;
            }
          });

          return maxWidth + 10;
        })
        .attr("height", !labels ? 0 : labels?.length * 20 * scale + 5)
        .style("cursor", "pointer")
        .style("fill", "var(--foreground)")
        .style("pointer-events", "none")
        .style("opacity", 1)
        .attr("rx", 4)
        .attr(
          "transform",
          "translate(-30," + -(margin.top + margin.bottom + 10 * scale) + ")",
        );
    }
  }, [randomData]);

  return <svg className="overflow-visible" ref={svgRef}></svg>;
});

HeatMap.displayName = "HeatMap";

export default HeatMap;
